import { get } from 'svelte/store';

import { format } from 'svelte-i18n';

// import { getAllStatuses, getAllSettings } from '../services/http';

//Stores
import { user_data, home_data, profile_data } from 'src/stores/account/user';
import { devices_status, devices_settings } from 'src/stores/device-runtime/status';
import { devices } from 'src/stores/lists/devices';
import { groups } from 'src/stores/lists/groups';
import { rooms } from 'src/stores/lists/rooms';
import { alarms, temp_schemes } from 'src/stores/lists/scenes-derived';
import {
	pending_connection_devices,
	z_wave_inclusion_statuses,
} from 'src/stores/device-runtime/firmware-ui';
import { scenes } from 'src/stores/lists/scenes';
import { device_ui } from 'src/stores/features/device-ui';
import { DeviceUIField } from 'src/types/DeviceUI';
//Design system components
import { toast } from '@Shelly/shelly-design-system';
//Helpers
import { getSettings, getStatus } from './get_status_settings_helpers';
import { is_gen_z, isWallDisplay, real_id } from './interface_helpers';
import { resetDrawerClose } from './drawer_helpers';
import { getDevicesStateByID } from 'src/services/iotu/v2_device_state';
import { socket_on_new_connection } from 'src/services/socket';
import { capFirstChar } from './utilities';
import { deleteProfileField, upsertProfileField } from './profile_data_helpers';
import { isPeripheralDevice } from './addons/addon_device_helpers';
import { sidePanelStateCombined } from 'src/stores/ui/sidepanel-public';

const lists = {
	devices: devices,
	rooms: rooms,
	alarms: alarms,
	temp_schemes: temp_schemes,
	groups: groups,
	scenes: scenes,
	user_data: user_data,
	home: home_data,
};

export const on_update = (data) => {
	console.log('%cdata', 'color: orange', data);

	// Handle DUI (Device UI) updates
	if (data?.changed === 'DUI' && data?.data) {
		const deviceId = data.data.id;
		if (deviceId) {
			// Get current device UI data for this device
			const currentDeviceUI = device_ui.getDeviceUI(deviceId);

			// Extract all the fields from data.data except 'id' and 'modified'
			const updateData = { ...data.data };
			delete updateData.id;
			delete updateData.modified;

			// Process each field in the update data
			Object.keys(updateData).forEach((key) => {
				// Check if this key exists in DeviceUIField enum
				const fieldKey = Object.values(DeviceUIField).find((field) => field === key);

				if (fieldKey) {
					// Update or add the field - the store will handle both cases
					device_ui.updateDeviceUIField(deviceId, fieldKey as any, updateData[key]);

					if (import.meta.env.DEV) {
						console.log('%cDUI Update', 'color: purple; font-size: 14px', {
							deviceId,
							field: key,
							action: currentDeviceUI.hasOwnProperty(key) ? 'updated' : 'added',
							value: updateData[key],
						});
					}
				} else if (import.meta.env.DEV) {
					console.warn(`DUI field '${key}' not found in DeviceUIField enum`, updateData[key]);
				}
			});
		}
		// Early return for DUI updates since they don't need the standard list update logic
		return;
	}

	if (
		typeof data?.deleted !== 'undefined' &&
		typeof get(devices)[data.deleted] !== 'undefined' &&
		get(sidePanelStateCombined)?.id === data.deleted
	) {
		//if there is a deleted device from other phone or pc or profile switching we need to reset the drawer in case we are in opened drawer situation
		resetDrawerClose();
	}
	// shared a device
	const deviceName = get(devices)[data.deleted]?.name;
	if (typeof data.device !== 'undefined') {
		toast.open({
			type: 'success',
			title: get(format)('shared_device_notification', {
				default: data.message,
			}),
		});
		update_list('devices', data.device);
		getDevicesStateByID(real_id(data.device.id));
		socket_on_new_connection(data.notifications_url);
		return;
	}

	const type = data?.changed?.toLowerCase() || data?.added?.toLowerCase();

	if (type === 'profile') {
		if (typeof data.deleted !== 'undefined') {
			profile_data.update((currentProfile) => deleteProfileField(currentProfile, data.deleted));
		} else if (data.id && typeof data.data !== 'undefined') {
			profile_data.update((currentProfile) =>
				upsertProfileField(currentProfile, data.id, data.data),
			);
		}
		return;
	}

	if (typeof lists[type] === 'undefined') return;

	if (
		typeof data.deleted !== 'undefined' &&
		(typeof deviceName !== 'undefined' || ['rooms', 'groups', 'scenes'].includes(type))
	) {
		let tranType = type.substring(0, type.length - 1);
		if (['rooms', 'groups'].includes(type)) {
			toast.open({
				type: 'success',
				title: capFirstChar(
					get(format)(`type_del_scc_toast`, {
						values: { this_type: get(format)(`process_delete_${tranType}`) },
					}) || `${capFirstChar(tranType)} is deleted successfully.`,
				),
			});
		} else if (type === 'scenes') {
			const thermostatsIds = Object.keys(get(temp_schemes));
			if (thermostatsIds.includes(data.deleted)) {
				toast.open({
					type: 'success',
					title: get(format)('alert_successfully_deleted_thermostat', {
						default: 'Successfully deleted thermostat',
					}),
				});
			} else {
				const scenesValues = Object.values(get(scenes));
				for (const sceneObj of scenesValues) {
					if (sceneObj.id === data.deleted && sceneObj?._gui_function === 'alarm') {
						tranType = 'alarm';
					}
				}
				toast.open({
					type: 'success',
					title: capFirstChar(
						get(format)(`type_del_scc_toast`, {
							values: { this_type: get(format)(`process_delete_${tranType}`) },
						}) || `${capFirstChar(tranType)} is deleted successfully.`,
					),
				});
			}
		}

		if (data.shared) {
			toast.open({
				type: 'danger',
				title: get(format)('unshared_device_notification', {
					values: { name: deviceName },
					default: 'The device {name} is no longer shared with you',
				}),
			});
		}
		delete_item(type, data.deleted);
	} else if (typeof data.added !== 'undefined') {
		const id = real_id(data.data.id);
		update_list(type, data.data);
		if (
			!/^(virtual_device)$/.test(data?.data?.category) &&
			!isPeripheralDevice(data?.data?.type) &&
			!isWallDisplay(data?.data?.type)
		) {
			pending_connection_devices.add(id);
		}

		if (data?.data?.gen === 'GBLE') {
			setTimeout(() => {
				if (!devices_status.getByRealId(id)) {
					getStatus(data.data);
				}
				if (!devices_settings.getByRealId(id)) {
					devices_settings.update_devices_settings(id, {
						device: {
							sleep_mode: true,
						},
						sleep_mode: {
							period: 86400,
							unit: 's',
						},
						_pending_cmds: 0,
					});
				}
			}, 2000);
		}
		if (is_gen_z(data.data)) {
			setTimeout(async () => {
				await getSettings(data.data);
				await getStatus(data.data);
			}, 1500);
		}
	} else if (typeof data.data !== 'undefined') {
		update_list(type, data.data, data.id);
		if (typeof data.data.including !== 'undefined') {
			z_wave_inclusion_statuses.add(data.fake_id, data.including);
		}
	} else if (type === 'home' && data.deleted && get(home_data)[data.deleted]) {
		home_data.update((home_data) => {
			home_data[data.deleted] = data;
			return home_data;
		});
	}
};

// We should not delete status and settings of multi-channel shared devices
// which still have other shared channels
const should_delete_status_settings = (id) => {
	const realId = real_id(id);
	if (id !== realId && get(devices)[realId]) {
		return false;
	}

	const device = get(devices)[id];
	if (device?.shared && device?.channels_count > 1) {
		for (let i = 0; i < device?.channels_count; i++) {
			let _id = i > 0 ? `${realId}_${i}` : realId;
			if (_id !== id && typeof get(devices)[_id] !== 'undefined') {
				return false;
			}
		}
	}

	return true;
};

const delete_item = (type, id) => {
	if (should_delete_status_settings(id)) {
		devices_status.remove(id);
		devices_settings.remove(id);
	}
	lists[type].remove(id);
};

const update_list = async (type, item, key?: string) => {
	try {
		if (import.meta.env.DEV) {
			console.log('Update lists [%s] [%s]', type, key, item);
		}
		if (item === null || typeof item === 'string') return;

		if (type === 'home') {
			home_data.update((curr) => ({
				...curr,
				...(key ? { [`${key}`]: item } : item),
			}));
			return;
		}
		lists[type].update(item);
	} catch (err) {
		console.log('List update error:', err, type, item);
	}
	// if (item.shared) {
	//   await getAllStatuses();
	//   await getAllSettings();
	// }
};
